import { useCallback, useState } from 'react';
import { PageStatus } from '@/types/store';
import { useReorderFunnelPages, useUpdateFunnelPage } from '../queries/funnel-page-mutations';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';
import type { FunnelPageItem, FunnelPageRouting } from '../types';

interface UseFunnelDetachPageArgs {
    storeId: number | undefined;
    savedFunnelPages: FunnelPageItem[];
}

/**
 * Detach a page from its parent's upsell/downsell branch and move it to the
 * end of the top-level list. Clears the parent's routing edge that pointed
 * at this page, then reorders so the detached page takes the last slot
 * (keeping orders sequential 1..N without duplicates).
 */
export function useFunnelDetachPage({ storeId, savedFunnelPages }: UseFunnelDetachPageArgs) {
    const updateFunnelPage = useUpdateFunnelPage(storeId ?? 0, true);
    const reorderFunnelPages = useReorderFunnelPages(storeId ?? 0);
    const [isDetaching, setIsDetaching] = useState(false);

    const handleDetachFromParent = useCallback(
        (pageId: number) => {
            if (!storeId) return;
            if (pageId === DRAFT_FUNNEL_PAGE_ID) return;

            const child = savedFunnelPages.find(p => p.page_id === pageId);
            if (!child || !child.referencePageId) return;

            let parent: FunnelPageItem | null = null;
            let event: 'on_purchase' | 'on_skip' | null = null;
            for (const candidate of savedFunnelPages) {
                if (candidate.page_id === pageId) continue;
                if (!candidate.routing) continue;
                if (candidate.routing.on_purchase?.page_id === pageId) {
                    parent = candidate;
                    event = 'on_purchase';
                    break;
                }
                if (candidate.routing.on_skip?.page_id === pageId) {
                    parent = candidate;
                    event = 'on_skip';
                    break;
                }
            }

            if (!parent || !event || !parent.referencePageId) return;

            setIsDetaching(true);

            const nextRouting: FunnelPageRouting | null = (() => {
                const cleared: FunnelPageRouting = {
                    on_purchase: parent.routing?.on_purchase ?? null,
                    on_skip: parent.routing?.on_skip ?? null,
                    [event]: null,
                };
                return cleared.on_purchase || cleared.on_skip ? cleared : null;
            })();

            const reorderedIds = [...savedFunnelPages.filter(p => p.page_id !== pageId).map(p => p.page_id), pageId];

            updateFunnelPage.mutate(
                {
                    page_id: parent.page_id,
                    referencePageId: parent.referencePageId,
                    productTitle: parent.title,
                    order: parent.order,
                    publish: parent.status === PageStatus.PUBLISHED,
                    topBannerText: parent.topBannerText,
                    funnelPrice: parent.funnelPrice,
                    funnelCtaText: parent.funnelCtaText,
                    routing: nextRouting,
                },
                {
                    onSuccess: () => {
                        const payload = reorderedIds.map((id, idx) => ({ page_id: id, order: idx + 1 }));
                        reorderFunnelPages.mutate(payload, { onSettled: () => setIsDetaching(false) });
                    },
                    onError: () => setIsDetaching(false),
                }
            );
        },
        [storeId, savedFunnelPages, updateFunnelPage, reorderFunnelPages]
    );

    return { handleDetachFromParent, isDetaching };
}
